import { ClockIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { BL_Warranty, nestApiGetAllMainGroups } from '@rabbit/bizproc/core';
import { AppContext } from '@rabbit/app-context';
import {
  AssociatedPartner,
  DTHoldingProxy,
  DTMainGroup,
  SELF_REGISTERED_VENDABLE,
  TenantInfo,
  WarrantyStatus,
  FBD_Retailer_Public,
} from '@rabbit/data/types';
import {
  ButtonIcon,
  FiltersModal,
  PillTime,
  Table,
  getClaimLimit,
  getCurrencyFormat,
  getWarrantyTerm,
  timestampToTimeAgo,
} from '@rabbit/elements/shared-components';
import { CreateTable } from '@rabbit/sage/utils/CreateTable';
import { RegistrationTableColumns } from '@rabbit/sage/utils/RegistrationTableColumns';
import {
  getProductTitleFromHoldingData,
  getWarrantyRemainingByDate,
  isValidDate,
  ONCE,
} from '@rabbit/sage/utils/helpers';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { Config_RegistrationTableType } from 'libs/configs/src/types/config-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { type MRT_ColumnDef } from 'material-react-table';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import { UserContext } from '@rabbit/sage/context/UserContext';

export interface TableRegistrationsProps {
  columns?: any;
  isLoading?: boolean;
  tenantType?: string;
  holdingList?: any;
  filters: FormValues;
  onFilter?: (value: FormValues) => void;
  isRegularTenant?: boolean;
}

export interface FormValues {
  voided?: boolean;
  dateType?: string;
  beginDate?: Date | undefined;
  endDate?: Date | undefined;
}

function TableRegistrations({
  isLoading,
  tenantType,
  holdingList,
  filters,
  onFilter,
  isRegularTenant = false,
}: TableRegistrationsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [mainGroups, setMainGroups] = useState<Map<string, string>>(new Map());
  const [retailerNames, setRetailerNames] = useState<Map<string, string>>(
    new Map()
  );
  const { config, tenantInfo } = useContext(AppContext);
  const { activeTenantInfo } = useContext(UserContext) ?? {};
  const { warrantorPersona, manufacturerPersona } = useGetMySagePersonas();
  const tenantLink = t('tenantLink');

  const isWarrantor = warrantorPersona !== null;
  const columns = useMemo(
    () => RegistrationTableColumns.Default(t, config),
    [t]
  );
  const carColumns = useMemo(() => RegistrationTableColumns.CarColumns, []);
  const tableTopColumns = useMemo(
    () => RegistrationTableColumns.TableTop(t, isWarrantor),
    [t, isWarrantor]
  );
  const supagardColumns = useMemo(
    () =>
      RegistrationTableColumns.Supagard(
        t,
        Boolean(manufacturerPersona?.manufacturer_private?.docid),
        config.HOLDINGS.REGISTRATION.SHOW_DEALER_GROUP
      ),
    [t, manufacturerPersona]
  );

  const getColumns = () => {
    if (tenantLink === 'SUPAGARD') {
      return supagardColumns;
    }
    switch (tenantType) {
      case 'carTenants':
        {
          /* // Price has been temporarily removed per DEV-1128 request -VP */
        }
        if (tenantLink === 'WARRANTYIRELAND') {
          return isRegularTenant
            ? carColumns.filter(
                (column: any) =>
                  column.accessorKey !== 'partner' &&
                  column.accessorKey !== 'price'
              )
            : carColumns.filter(
                (column: any) =>
                  column.accessorKey !== 'salesPerson' &&
                  column.accessorKey !== 'price'
              );
        } else {
          return isRegularTenant
            ? carColumns.filter(
                (column: any) => column.accessorKey !== 'partner'
              )
            : carColumns.filter(
                (column: any) => column.accessorKey !== 'salesPerson'
              );
        }
      case 'tableTenants':
        return isRegularTenant
          ? tableTopColumns.filter(
              (column: any) => column.accessorKey !== 'partner'
            )
          : tableTopColumns;
      default:
        return isRegularTenant
          ? columns.filter((column: any) => column.accessorKey !== 'partner')
          : columns;
    }
  };

  const onChangeFilters = (value: {
    beginDate?: Date | undefined;
    endDate?: Date | undefined;
  }) => {
    setIsFilterModalOpen(false);
    if (onFilter) {
      onFilter(value);
    }
  };

  const BikeRegistrationTable = useMemo(
    () =>
      CreateTable<DTHoldingProxy>(holdingList.data || [], [
        {
          header: 'Timestamp',
          Header: <ClockIcon className="h-4 w-4" />,
          Cell: ({ cell }: any) => {
            return (
              <PillTime
                label={
                  cell.getValue() ? timestampToTimeAgo(cell.getValue()) : ''
                }
                className={'bg-gray-300 text-gray-900'}
              />
            );
          },
          value: (holding) => holding.tcreate,
        },
        ...(config.HOLDINGS.REGISTRATION_DETAILS_VIEW.COMPANY_NAME
          ? [
              {
                header: t('general.businessName'),
                value: (holding: any) => holding.business_name ?? '-',
              },
            ]
          : []),
        {
          header: t('general.id'),
          value: (holding) => holding.docid?.slice(0, 5).toUpperCase(),
        },
        {
          header: t('general.customerName'),
          value: (holding) => holding.consumer_name,
        },
        {
          header: t('general.product'),
          primary: true,
          value: (holding) => getProductTitleFromHoldingData(holding),
        },
        {
          header: t('general.serialNumber'),
          value: (holding) => holding.serial || '-',
        },
        {
          header: t('general.modelNumber'),
          value: (holding) =>
            holding?.mpn && holding?.mpn !== 'n/a' ? holding.mpn : '-',
        },
        {
          header: t('general.purchaseDate'),
          type: 'Date',
          value: (holding) => holding.purchase_time || '-',
        },
        {
          header: t('general.registered'),
          type: 'Date',
          value: (holding) => holding.register_time || '-',
        },
        ...(config.HOLDINGS.REGISTRATION_DETAILS_VIEW.PURCHASE_PRICE
          ? [
              {
                header: t('general.purchasePrice'),
                value: (holding: any) =>
                  holding.decider?.decided?.totalPrice
                    ? getCurrencyFormat(
                        holding.decider?.decided?.totalPrice as number,
                        tenantInfo?.currency as string
                      )
                    : '-',
              },
            ]
          : []),
        {
          header: t('general.expiring'),
          type: 'Date',
          value: (holding) =>
            BL_Warranty.getLatestWarranty(holding.warranties)?.endDate || '-',
        },
        {
          header: t('general.termLeft'),
          value: (holding) => {
            const warranty = BL_Warranty.getLatestWarranty(holding.warranties);
            return warranty?.endDate && warranty.startDate
              ? getWarrantyRemainingByDate(
                  warranty?.endDate,
                  warranty?.startDate
                )
              : '-';
          },
        },
      ]),
    [holdingList.data, t]
  );

  const CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT =
    t('CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT') === 'true';

  useEffect(() => {
    const fetchRetailerNames = async () => {
      try {
        const data: any[] = holdingList?.data || [];
        const idsSet = new Set<string>();
        data.forEach((h: any) => {
          const id = h?.purchase_location;
          if (id) idsSet.add(id);
        });
        const ids = Array.from(idsSet);
        const newMap = new Map(retailerNames);
        const retailers = await FBD_Retailer_Public.query()
          .where('docid', 'in', ids)
          .getContents();
        retailers.forEach((r: any) => {
          if (r?.docid) newMap.set(r.docid, r.name || '-');
        });
        setRetailerNames(newMap);
      } catch (e) {
        // Silently ignore fetch issues to avoid breaking the table
        // console.error('Failed to fetch retailer names', e);
      }
    };
    if (holdingList?.data && !isLoading) {
      void fetchRetailerNames();
    }
  }, [holdingList?.data]);

  useEffect(() => {
    if (!isLoading && holdingList.data) {
      const getTableData = () => {
        switch (tenantType) {
          case 'carTenants':
            return formatCarResults(
              holdingList?.data,
              CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT,
              tenantInfo,
              retailerNames
            ).filter((i) => i !== null);
          case 'tableTenants':
            return formatTableCoverResults(
              holdingList?.data,
              tenantInfo
            ).filter((i) => i !== null);
          default:
            return formatResults(
              holdingList?.data,
              config,
              tenantLink,
              mainGroups,
              activeTenantInfo?.info?.name,
              retailerNames
            ).filter((i) => i !== null);
        }
      };
      setTableData(getTableData());
    }
  }, [holdingList.data, isLoading, t, mainGroups, retailerNames]);

  useEffect(() => {
    const getMainGroups = async () => {
      const mainGroupList: any = await nestApiGetAllMainGroups();
      console.log(mainGroupList, 'CONWSLING ');
      const mainGroupSet = new Map<string, string>();
      if (mainGroupList.length > 0) {
        mainGroupList.forEach((item: DTMainGroup) => {
          mainGroupSet.set(item.docid, item.title);
          item.associatedPartners?.forEach((partners: AssociatedPartner) => {
            mainGroupSet.set(
              partners.tenantLink.length > 0
                ? partners.tenantLink
                : partners.businessUserInviteLink,
              item.title
            );
          });
        });
      }
      setMainGroups(mainGroupSet);
    };

    getMainGroups();
  }, [config.HOLDINGS.REGISTRATION.SHOW_DEALER_GROUP]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="w-full">
        <div className="relative z-10 flex w-full items-center justify-between">
          {!config.HOLDINGS.REGISTRATION.ADVANCED_TABLE && (
            <div className="relative pb-4 md:absolute md:top-[25px] md:right-5">
              {!isLoading && (
                <ButtonIcon
                  label={t('general.filters')}
                  iconLeft={true}
                  Icon={FunnelIcon}
                  onClick={() => setIsFilterModalOpen(true)}
                  kind={'bgLightGreen'}
                  count={
                    (filters && filters.beginDate) ||
                    (filters && filters.endDate)
                      ? 1
                      : 0
                  }
                />
              )}
            </div>
          )}
          {isFilterModalOpen && (
            <div className="absolute right-0 top-16 z-10">
              <FiltersModal
                page={'registrations'}
                handleClose={() => setIsFilterModalOpen(false)}
                data={filters}
                onChange={onChangeFilters}
              />
            </div>
          )}
        </div>
        {config.HOLDINGS.REGISTRATION.TABLE_TYPE ===
          Config_RegistrationTableType.BIKE_TYPE && (
          <div className="relative z-0">
            <BikeRegistrationTable
              initialState={{
                columnPinning: { right: ['actions'] },
                showGlobalFilter: true,
                showColumnFilters: true,
                sorting: [
                  {
                    id: 'timestamp',
                    desc: true,
                  },
                ],
              }}
              muiSearchTextFieldProps={{
                placeholder: t('general.searchByRegistrationIDss'),
              }}
              state={{
                isLoading: isLoading,
              }}
              onClickRow={(holding) =>
                ONCE(
                  () =>
                    navigate(
                      SAGE_ROUTE_NAME.REGISTRATIONS + '/' + holding.docid,
                      {
                        state: {
                          warrantyId: BL_Warranty.getLatestWarranty(
                            holding.warranties
                          )?.warrantyLink,
                          title: getProductTitleFromHoldingData(holding),
                          link: holding.docid,
                        },
                        replace: true,
                      }
                    ),
                  2000
                )()
              }
            />
          </div>
        )}
        {config.HOLDINGS.REGISTRATION.TABLE_TYPE ===
          Config_RegistrationTableType.DEFAULT && (
          <div className="relative z-0">
            <Table
              columns={getColumns()}
              data={tableData}
              initialState={{
                columnPinning: { right: ['actions'] },
                showGlobalFilter: true,
                sorting: [
                  {
                    id: 'timeStamp',
                    desc: false,
                  },
                ],
              }}
              enableColumnFilters={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableTopToolbar={!config.HOLDINGS.REGISTRATION.ADVANCED_TABLE}
              advancedTable={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableRowSelection={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableBottomToolbar={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableToolbarInternalActions={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              enableFacetedValues={Boolean(
                config.HOLDINGS.REGISTRATION.ADVANCED_TABLE
              )}
              columnFilterDisplayMode="popover"
              muiSearchTextFieldProps={{
                placeholder: t('general.searchByRegistrationID'),
              }}
              useCallbackClickRow={(data: {
                warrantyId: string;
                product: string;
                registrationPlate: string;
                make: string;
                link: string;
                id: string;
                modelNum: string;
              }) =>
                ONCE(
                  () =>
                    navigate(SAGE_ROUTE_NAME.REGISTRATIONS + '/' + data.link, {
                      state: {
                        warrantyId:
                          tenantType === 'tableTenants'
                            ? data.id
                            : data.warrantyId,
                        title:
                          tenantType === 'carTenants'
                            ? data.make + ' ' + data.registrationPlate
                            : data.product,
                        link: data.link,
                        modelNum: data.modelNum,
                      },
                    }),
                  2000
                )()
              }
              state={{
                isLoading: isLoading,
              }}
            />
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
}

export default TableRegistrations;

/** HELPERS **/

const extractRetailerDocIdFromLink = (link?: string): string | null => {
  if (!link) return null;
  const parts = String(link).split(':');
  const candidate = parts.length > 1 ? parts[parts.length - 1] : String(link);
  const id = candidate.trim();
  return /^[sS]/.test(id) ? id : null;
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const formatResults = (
  results: any[],
  config: any,
  tenantType: string,
  mainGroupSets: Map<string, string>,
  defaultGroup: string | undefined,
  retailerNameMap: Map<string, string>
) => {
  const formattedResults = results.map((result, i) => {
    if (result.vendable === SELF_REGISTERED_VENDABLE) return null;
    const warrantyInfo = BL_Warranty.getLatestWarranty(result.warranties);
    return {
      timeStamp: (
        <PillTime
          label={
            result.register_time
              ? timestampToTimeAgo(result?.register_time)
              : ''
          }
          className={'bg-gray-300 text-gray-900'}
        />
      ),
      warrantyId: result.docid.slice(0, 5).toUpperCase(),
      registered: isValidDate(new Date(result?.register_time))
        ? result.register_time
        : undefined,
      expiring: warrantyInfo?.endDate,
      term: getWarrantyTerm(result),
      ...(config.HOLDINGS.INSTALLATION_REQUIRED && {
        installer: result.installation?.installer
          ? result.installation.installer.first_name
            ? result.installation.installer.first_name +
              ' ' +
              result.installation.installer.last_name
            : result.installation.installer.name ?? 'Unassigned'
          : 'Unassigned',
      }),
      product: getProductTitleFromHoldingData(result),
      modelNum: result?.mpn && result?.mpn !== 'n/a' ? result.mpn : '-',
      serialNum: result.serial ? result.serial : '-',
      purchaseDate:
        result?.purchase_time && result?.purchase_time > 0
          ? result?.purchase_time
          : undefined,
      extended: 'No', //TODO
      // actions: (
      //   <div className="flex items-center justify-center">
      //     <TrashIcon
      //       className="w-5 h-5 cursor-pointer"
      //       onClick={() => {
      //         setdeleteRegistration(!deleteRegistration);
      //         // onDeleteRegistration(result.docid);
      //       }}
      //     />
      //   </div>
      // ),
      link: result.docid,
      consumerName: result.consumer_name || '-',
      salesPerson: result?.additionalData?.salesperson || '-',
      dealerGroup:
        mainGroupSets.get(warrantyInfo?.retailerLink?.split(':')[1] || '') ||
        defaultGroup ||
        '-',
      registrationNumber: result?.additionalData?.vehicleNo || '-',
      retailerName:
        retailerNameMap.get(result?.purchase_location) ??
        (result?.purchase_location_other || '-'),
    };
  });
  return formattedResults;
};

const formatCarResults = (
  results: any[],
  CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT: boolean,
  tenantInfo: TenantInfo | undefined,
  retailerNameMap: Map<string, string>
) => {
  return results.map((result, i) => {
    const vehicleInfo =
      result.self_registration?.srvInfo?.productInfo ??
      result.self_registration?.vehicleInfo; // TODO: make it more flexible for other tenants as part of DEV-528
    const warrantyInfo = BL_Warranty.getLatestWarranty(result.warranties);
    const voided = warrantyInfo?.status === WarrantyStatus.VOIDED;
    return {
      timeStamp: (
        <PillTime
          label={
            result.tcreate
              ? timestampToTimeAgo(new Date(result?.tcreate).getTime())
              : ''
          }
          className={'bg-gray-300 text-gray-900'}
        />
      ),
      warrantyId: result.docid.slice(0, 5).toUpperCase(),
      registrationPlate: BL_Warranty.getVehicleRegistrationNo(vehicleInfo),
      make: vehicleInfo.brand || '-',
      registered: result.tcreate,
      start: warrantyInfo?.startDate,
      expiring: warrantyInfo?.endDate,
      warrantyPlan: voided ? 'voided' : warrantyInfo?.templateTitle || '-',
      term: voided ? '-' : getWarrantyTerm(result),
      claimLimit: voided
        ? '-'
        : getClaimLimit(
            warrantyInfo,
            tenantInfo?.currency,
            CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT
          ) || '-',
      partner: warrantyInfo?.retailerName,
      salesPerson: warrantyInfo?.salesRep ?? '',
      price:
        result.purchase_price && warrantyInfo?.price
          ? getCurrencyFormat(
              warrantyInfo?.price?.amount,
              warrantyInfo?.price?.currency
            ) +
            ` (${getCurrencyFormat(
              warrantyInfo?.price?.amount * 1.2,
              warrantyInfo?.price?.currency
            )} incl. VAT)`
          : '-',
      link: result.docid,
      customerName: result.consumer_name || '-',
    };
  });
};

const formatTableCoverResults = (results: any[], tenantInfo: any) =>
  results.map((result, i) => {
    const warrantyInfo = BL_Warranty.getLatestWarranty(result.warranties);
    const productInfo = result.self_registration.srvInfo.productInfo;
    const voided = warrantyInfo?.status === WarrantyStatus.VOIDED;

    const purchasePrice = warrantyInfo?.decider?.decided?.totalPrice ?? 0;

    return {
      timeStamp: (
        <PillTime
          label={
            result.created_time
              ? timestampToTimeAgo(new Date(result?.created_time).getTime())
              : ''
          }
          className={'bg-gray-300 text-gray-900'}
        />
      ),
      id: result.docid.slice(0, 5).toUpperCase(),
      product: productInfo.typeofStone,
      area: productInfo.surfaceAreaValue
        ? productInfo.surfaceAreaValue + 'm²'
        : 'N/A',
      registered: result.tcreate,
      start: warrantyInfo?.startDate,
      expiring: warrantyInfo?.endDate,
      warrantyPlan: voided
        ? 'voided'
        : warrantyInfo?.templateTitle?.replace(' warranty', '') || '-',
      term: voided ? '-' : getWarrantyTerm(result),
      partner: warrantyInfo?.retailerName,
      salesperson: warrantyInfo?.salesRep ?? '-',
      purchasePrice: purchasePrice
        ? getCurrencyFormat(purchasePrice as number, tenantInfo?.currency)
        : '0',
      link: result.docid,
      customerName: result.consumer_name || '-',
      sealing: productInfo.upsellSealing ? 'Yes' : 'No',
    };
  });
